/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import MsDyn365 from '@msdyn365-commerce/core';

import { IModelViewerData } from './model-viewer.data';
import { IModelViewerProps } from './model-viewer.props.autogenerated';
import { getAriAssemblyDetailsAsync, getManufacturerBrandNameAsync } from '../../actions/DataActionExtension.g';
import { IAriServiceAssemblyInformation, IAriServiceAssemblyPart } from '../../actions/DataServiceEntities.g';
import { ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';
import { AssemblyDropdownComponent } from './components/assemblyDropdown.component';
import { AriHotspotImage } from './components/AriHotspotImage.component';
import { AriPartsListTableComponent } from './components/AriPartsListTable.component';
import { ProductSearchResult, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import * as _ from 'lodash';
import getPartStateAction, { GetPartStateInput, IProductSearchResultWithState } from '../../actions/get-part-state.action';
import getExtensionPropertyValueByKey from '../../utils/getExtensionPropertyValueByKey';
import { CommercePropertyValueEnum } from '../../modules/allowable-freight/utils/CommercePropertyValueEnum';
import { IGetModelAssemblyData } from '../../actions/get-model-assembly.action';

/**
 * Props for the view component
 */
export interface IModelViewerViewProps extends IModelViewerProps<IModelViewerData> {
    assemblyList: React.ReactNode;
    hotspotImage: React.ReactNode | null;
    partsList: React.ReactNode | null;
    baseClassName: string;
    heading: React.ReactNode | null;
    isMobile: boolean;
}

export interface IAriServiceAssemblyPartProductCombinations extends IAriServiceAssemblyPart {
    Product?: IProductSearchResultWithState;
}

type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 * State for the component
 */
interface IModelViewerState {
    assemblyDetail: IAriServiceAssemblyInformation | undefined;
    isPartResponseError: boolean;
    selectedRow: string | undefined;
    isDrawerOpen: boolean;
    ariBrandName: string;
    manufacturerCode: string;
    gotProductData: boolean;
    assemblyList: IGetModelAssemblyData | undefined;
    expandedNotes: Record<string, boolean>;
}

export interface RowFeedback {
    message: string;
    success: boolean;
}

export enum SupersessionKey {
    SupersededId = 'superseded-from-id',
    SupersededName = 'superseded-from-name',
    Inventory = 'inventory'
}

/**
 *
 * ModelViewer component
 * @extends {React.PureComponent<IModelViewerProps<IModelViewerData>>}
 */
@observer
class ModelViewer extends React.PureComponent<IModelViewerProps<IModelViewerData>, IModelViewerState> {
    /**
     * Base class name for the elements on the page
     * @private
     */
    private _baseClassName = 'ari-model-viewer';
    /**
     * React Ref pointing to the transition component
     * @private
     */
    private _transformRef = React.createRef<ReactZoomPanPinchRef>();
    /**
     * Id prefix for the part
     * @private
     */
    private _partIdPrefix = 'part__';
    /**
     * Id prefix for the tag
     * @private
     */
    private _tagIdPrefix = 'ref__';

    @observable
    private _viewport: GridSize;

    constructor(props: IModelViewerProps<IModelViewerData>) {
        super(props);

        this._viewport =
            props.context.request && props.context.request.device && props.context.request.device.Type === 'Mobile' ? 'xs' : 'lg';

        this.state = {
            assemblyDetail: undefined,
            isPartResponseError: false,
            selectedRow: undefined,
            isDrawerOpen: false,
            ariBrandName: '',
            manufacturerCode: '',
            gotProductData: false,
            assemblyList: undefined,
            expandedNotes: {}
        };

        this._updateViewport = this._updateViewport.bind(this);
        this.getAssemblyDetails = this.getAssemblyDetails.bind(this);
        this.goToId = this.goToId.bind(this);
        this.goToPart = this.goToPart.bind(this);
        this.closePartDrawer = this.closePartDrawer.bind(this);
        this.renderAssemblyHeader = this.renderAssemblyHeader.bind(this);
        this.addPartToCart = this.addPartToCart.bind(this);
    }

    @computed get isMobile(): boolean {
        // return this._viewport === 'xs' || this._viewport === 'sm';
        return this._viewport === 'xs';
    }

    public async componentDidMount(): Promise<void> {
        const {
            context: {
                request: { query }
            }
        } = this.props;
        if (MsDyn365.isBrowser) {
            window.addEventListener('resize', this._updateViewport);
            this._updateViewport();
        }
        void this._getManufacturerCodeName(query!.brandCode);
        this.setState({ assemblyList: await this.props.data.assemblyList });
        console.log(this.state.assemblyList);
    }

    public componentWillUnmount(): void {
        if (MsDyn365.isBrowser) {
            window.removeEventListener('resize', this._updateViewport);
        }
    }

    /**
     * Renders the assembly header
     * @private
     */
    private renderAssemblyHeader(renderToolButtonRow: JSX.Element | null) {
        const {
            // data: {
            //     assemblyList: { result }
            // },
            resources: { defaultAssemblyOption, selectedOptionPrefix }
        } = this.props;
        const { assemblyDetail } = this.state;
        const isMobileClassName = this.isMobile ? `${this._baseClassName}__header--mobile` : '';
        const result = this.state.assemblyList;
        if (!result || !result.ariAssemblyData || result.ariAssemblyData.length < 1) {
            return (
                <div className={`${this._baseClassName}__model-error`}>
                    <span>{this.props.resources.modelErrorText}</span>
                </div>
            );
        } else {
            return (
                <div className={`${this._baseClassName}__header ${isMobileClassName}`}>
                    {assemblyDetail ? (
                        <div className={`${this._baseClassName}__header--title`}>
                            <h3>
                                {selectedOptionPrefix} {assemblyDetail?.Name}
                            </h3>
                        </div>
                    ) : null}
                    {result.ariAssemblyData && result.ariAssemblyData.length > 0 ? (
                        <div className={`${this._baseClassName}__header--details`}>
                            <div className={`${this._baseClassName}__label`}>
                                <h3>{defaultAssemblyOption}</h3>
                            </div>
                            <AssemblyDropdownComponent
                                baseClassName={this._baseClassName}
                                assemblyData={result.ariAssemblyData}
                                onAssemblySelect={this.getAssemblyDetails}
                                defaultOptionString={defaultAssemblyOption}
                            />
                            {renderToolButtonRow}
                        </div>
                    ) : null}
                </div>
            );
        }
    }

    /**
     * Renders the page title
     * @private
     */
    private renderTitle() {
        const {
            data: {
                assemblyList: { result }
            }
        } = this.props;
        if (result && result.ariAssemblyData?.length) {
            const firstAssembly = result.ariAssemblyData[0];
            return (
                <div className={`${this._baseClassName}-title-wrapper`}>
                    <h1 className={`${this._baseClassName}-title-wrapper__title`}>{`${
                        this.state.ariBrandName ? `${this.state.ariBrandName} - ` : ''
                    }${firstAssembly.ParentName}`}</h1>
                </div>
            );
        }
        return null;
    }

    private async _getManufacturerCodeName(brandCode: string): Promise<void> {
        const codesResults = this.props.data.manufacturerCodes.result ? this.props.data.manufacturerCodes.result : [];

        if (codesResults.length === 0) {
            return;
        }
        try {
            const response = await getManufacturerBrandNameAsync({ callerContext: this.props.context.actionContext }, brandCode);
            this.setState({ ariBrandName: response[0].Description || '', manufacturerCode: response[0].ManufacturerCode || '' });
        } catch (err) {
            console.error(err);
        }
    }

    /**
     * Gets assembly details from the data service
     * @param assemblyId Assembly Id to get details for
     * @private
     */
    private async getAssemblyDetails(assemblyId: number) {
        if (assemblyId === 0) {
            return;
        }
        const {
            context: {
                request: {
                    // @ts-ignore
                    query: { brandCode, modelId }
                }
            }
        } = this.props;
        try {
            const response = await getAriAssemblyDetailsAsync(
                { callerContext: this.props.context.actionContext },
                brandCode,
                parseInt(modelId, 10),
                assemblyId
            );

            this.setState(
                {
                    assemblyDetail: response
                },
                () => this.loadProductMappings()
            );
        } catch (err) {
            this.setState({ isPartResponseError: true });
        }
    }

    private async loadProductMappings() {
        const mfrCode = this.state.manufacturerCode;
        const baseParts = this.state.assemblyDetail?.Parts;

        if (!baseParts) {
            return;
        }

        const mfrCodes: string[] = [];

        if (mfrCode === 'HRT' || mfrCode === 'TTI' || mfrCode === 'HOM') {
            mfrCodes.push(...['HOM', 'HRT', 'TTI'].filter(c => c !== mfrCode));
        }
        mfrCodes.unshift(mfrCode);
        const partIds: string[] = baseParts.flatMap(p => {
            return mfrCodes.map(m => {
                return `${m}${p.Sku}`;
            });
        });

        if (partIds && Array.isArray(partIds)) {
            const promises = await this.getProducts(partIds);
            const response = _.flatten(promises);

            const partproducts: IAriServiceAssemblyPartProductCombinations[] = [];
            const parts = baseParts?.slice() as IAriServiceAssemblyPartProductCombinations[];

            parts?.forEach(part => {
                const codes = mfrCodes.map(m => `${m}${part.Sku || ''}`);
                const responseProduct = response.find(p => {
                    const supersededIdRes = getExtensionPropertyValueByKey(
                        p.ExtensionProperties,
                        SupersessionKey.SupersededId,
                        CommercePropertyValueEnum.string
                    );
                    const supersededId = typeof supersededIdRes === 'string' ? supersededIdRes : undefined;
                    const foundCode = codes.find(c => p.ItemId === c || (supersededId && supersededId === c));
                    return foundCode ? p : null;
                    // return p.ItemId === code || (supersededId && supersededId === code);
                });
                partproducts.push({ ...part, Product: responseProduct });
            });

            const stateAssemblyCopy = this.state.assemblyDetail;

            if (stateAssemblyCopy) {
                stateAssemblyCopy.Parts = partproducts;

                this.setState({
                    assemblyDetail: stateAssemblyCopy,
                    gotProductData: true
                });
            }
        }
    }

    private async getProducts(partIds: string[]): Promise<IProductSearchResultWithState[]> {
        const cart = await this.props.data.cart;
        const cartId = cart.cart.Id;
        const addressId = cart.cart.ShippingAddress?.RecordId || 0;
        const input = new GetPartStateInput(partIds, addressId, cartId, this.props.context.request.channel!.RecordId);
        const partresponse = await getPartStateAction(input, this.props.context.actionContext);

        // needed to get list of products excluded from criteriaResponse data action in getPartStateAction
        const missingProducts = [];
        if (this.state.assemblyDetail?.Parts) {
            for (let i = 0; i < this.state.assemblyDetail?.Parts?.length; i++) {
                let found = false;
                for (let j = 0; j < partresponse.productResults.length; j++) {
                    // if (partresponse.productResults[j].ItemId === this.state.assemblyDetail?.Parts[i].PartId) {
                    if (partresponse.productResults[j].ItemId?.substring(3) === this.state.assemblyDetail?.Parts[i].Sku) {
                        found = true;
                        break;
                    }
                }
                if (!found) {
                    const tempProduct: IProductSearchResultWithState = {
                        ItemId: `${this.state.manufacturerCode}${this.state.assemblyDetail?.Parts[i].Sku}`,
                        Name: this.state.assemblyDetail?.Parts[i].Description,
                        Description: this.state.assemblyDetail?.Parts[i].Description,
                        Price: this.state.assemblyDetail?.Parts[i].MSRP,
                        RecordId: 0
                    };
                    missingProducts.push(tempProduct);
                }
            }
            //     end get missing list of products
        }
        return partresponse.productResults.concat(missingProducts);
    }

    /**
     * Renders the hotspot image
     * @private
     */
    private renderHotspotImage() {
        const {
            resources: {
                assemblyOptionsPlaceholderText,
                centerButtonText,
                resetButtonText,
                zoomInButtonText,
                zoomOutButtonText,
                hotspotUsageHelpText
            }
        } = this.props;
        const resources = {
            centerButtonText,
            resetButtonText,
            zoomInButtonText,
            zoomOutButtonText,
            hotspotUsageHelpText,
            assemblyOptionsPlaceholderText
        };
        return (
            <AriHotspotImage
                renderHeader={this.renderAssemblyHeader}
                assemblyDetailState={this.state.assemblyDetail}
                resources={resources}
                baseClassName={this._baseClassName}
                // ImageUrl={ImageUrl}
                // HotSpots={HotSpots}
                // Name={Name}
                transformRef={this._transformRef}
                goToPart={this.goToPart}
                tagIdPrefix={this._tagIdPrefix}
                isMobile={this.isMobile}
            />
        );
    }

    /**
     * Renders the parts list
     * @private
     */
    private renderPartsList() {
        if (this.state.assemblyDetail === undefined) {
            return null;
        }
        const {
            state: {
                assemblyDetail: { Parts },
                selectedRow,
                isDrawerOpen
            },
            _baseClassName,
            goToId,
            addPartToCart,
            closePartDrawer,
            _partIdPrefix
        } = this;

        const {
            resources: {
                refIdColHeading,
                partNoColHeading,
                descriptionColHeading,
                availabilityColHeading,
                yourPriceColHeading,
                listPriceColHeading,
                qtyColHeading,
                loginToViewPricingText,
                addToCartText,
                unavailableText,
                orderColHeading,
                contextNoteText,
                showLessText,
                showMoreText
            },
            context: {
                request: {
                    user: { isAuthenticated, signInUrl }
                },
                cultureFormatter: { formatCurrency }
            }
        } = this.props;
        const tableHeadings = [
            refIdColHeading,
            partNoColHeading,
            descriptionColHeading,
            availabilityColHeading,
            listPriceColHeading,
            yourPriceColHeading,
            qtyColHeading,
            orderColHeading
        ];

        return this.state.gotProductData ? (
            <AriPartsListTableComponent
                isDoNotAllowBackOrder={this.props.context.app.config.doNotAllowBackOrder || false}
                oosInvenLabel={this.props.context.app.config.outOfStockMessage || 'Temporarily out of stock'}
                parts={Parts || []}
                tableHeadings={tableHeadings}
                baseClassName={_baseClassName}
                goToId={goToId}
                addPartToCart={addPartToCart}
                partIdPrefix={_partIdPrefix}
                selectedRow={selectedRow}
                requiresAuthentication={this.props.config.onlyShowPriceWhenAuthenticated || false}
                isAuthenticated={isAuthenticated}
                loginToViewPricingText={loginToViewPricingText}
                signInUrl={signInUrl || ''}
                isMobile={this.isMobile}
                isDrawerOpen={isDrawerOpen}
                closePartDrawer={closePartDrawer}
                addToCartText={addToCartText}
                unavailableText={unavailableText}
                priceFormatter={formatCurrency}
                mfrCode={this.state.manufacturerCode}
                contextNotePart={contextNoteText}
                maxCharLimit={this.props.config.maxCharacterLimit || 75}
                showLessText={showLessText}
                showMoreText={showMoreText}
            />
        ) : (
            <></>
        );
    }

    /**
     * Scrolls the page to the hotspot on the image after clicking the ref in the part list, causes the hotspot to flash
     * @param id id of the hotspot to go to
     * @private
     */
    private async goToId(id: string) {
        if (this._transformRef?.current?.zoomToElement) {
            const target = `${this._tagIdPrefix}${id}`;
            window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
            await this.waitForScroll();
            const elementList = document.querySelectorAll<HTMLSpanElement>(`#${target}`);
            this._transformRef.current.zoomToElement(target, elementList.length === 1 ? 2 : 1);
            if (elementList.length) {
                elementList.forEach(el => {
                    const classToAdd = `${this._baseClassName}__part-flash`;
                    el.classList.add(classToAdd);
                    setTimeout(() => el.classList.remove(classToAdd), 4000);
                });
            }
        }
    }

    /**
     * Scrolls the page to the hotspot on the image after clicking the ref in the part list, causes the hotspot to flash
     * @param id id of the hotspot to go to
     * @private
     */
    private async addPartToCart(part: ProductSearchResult, partId: string, partQty: number) {
        let cart;
        let partEl: HTMLElement | null;

        if (!part || !partId) {
            return; // {"message":"Sorry,there was an error retrieving the part.","success": false};
        }

        partEl = document.querySelector(`p.hide-status-${partId}`);
        // partQty = parseInt((document.getElementById(partId) as HTMLInputElement).value, 10);
        cart = await this.props.data.cart;

        if (!cart) {
            if (partEl) {
                partEl.textContent = this.props.resources.errorRetrievingCart;
                partEl.style.display = 'block';
                setTimeout(() => {
                    partEl!.style.display = 'none';
                }, 7000);
            }
        }

        const product: SimpleProduct = {
            AdjustedPrice: part.Price,
            ProductTypeValue: 0,
            BasePrice: part.BasePrice || 0,
            Name: part.Name,
            Price: part.Price,
            ItemId: part.ItemId,
            RecordId: part.RecordId,
            ExtensionProperties: part.ExtensionProperties
        };

        await cart
            .addProductToCart({ product, count: partQty, catalogId: 0 })
            .then(response => {
                if (response.status === 'SUCCESS') {
                    if (partEl) {
                        partEl.textContent = this.props.resources.addToCartSuccess;
                        partEl.style.display = 'block';

                        setTimeout(() => {
                            partEl!.style.display = 'none';
                        }, 7000);
                    }
                } else {
                    if (partEl) {
                        partEl.textContent = this.props.resources.errorAddingtoCart;
                        partEl.style.display = 'block';

                        setTimeout(() => {
                            partEl!.style.display = 'none';
                        }, 7000);
                    }
                }
            })
            .catch(() => {
                if (partEl) {
                    partEl.textContent = this.props.resources.errorAddingtoCart;
                    partEl.style.display = 'block';

                    setTimeout(() => {
                        partEl!.style.display = 'none';
                    }, 7000);
                }
            });
    }

    private goToPart(id: string) {
        this.setState({ selectedRow: id, isDrawerOpen: true });
    }

    private closePartDrawer() {
        this.setState({
            isDrawerOpen: false
        });
    }

    /**
     * Waits for scrolling to finish and returns a promise when it does.
     */
    private waitForScroll(): Promise<void> {
        let lastChangedFrame = 0;
        let lastX = window.scrollX;
        let lastY = window.scrollY;
        return new Promise<void>(resolve => {
            function tick(frames: number) {
                if (frames >= 500 || frames - lastChangedFrame > 20) {
                    resolve();
                } else {
                    if (window.scrollX !== lastX || window.scrollY !== lastY) {
                        lastChangedFrame = frames;
                        lastX = window.scrollX;
                        lastY = window.scrollY;
                    }
                    requestAnimationFrame(tick.bind(null, frames + 1));
                }
            }
            tick(0);
        });
    }

    private _updateViewport(): void {
        this._viewport = this._getViewport();
    }

    private readonly _getViewport = (): GridSize => {
        const { context } = this.props;

        if (MsDyn365.isBrowser && window.innerWidth) {
            const gridSettings = context.request.gridSettings;
            if (gridSettings) {
                if (gridSettings.xs && window.innerWidth <= gridSettings.xs.w) {
                    return 'xs';
                } else if (gridSettings.sm && window.innerWidth <= gridSettings.sm.w) {
                    return 'sm';
                } else if (gridSettings.md && window.innerWidth <= gridSettings.md.w) {
                    return 'md';
                } else if (gridSettings.lg && window.innerWidth <= gridSettings.lg.w) {
                    return 'lg';
                }
                return 'xl';
            }
        }

        return 'xs';
    };

    public render(): JSX.Element | null {
        const viewProps: IModelViewerViewProps = {
            ...this.props,
            assemblyList: this.renderAssemblyHeader(null),
            hotspotImage: this.renderHotspotImage(),
            partsList: this.renderPartsList(),
            heading: this.renderTitle(),
            baseClassName: this._baseClassName,
            isMobile: this.isMobile
        };
        return this.props.renderView(viewProps);
    }
}

export default ModelViewer;
